// src/modules/app-views/crm/pages/Document/tabs/RecordTab/ContactCollapse.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Collapse, Button, Space, Tooltip } from 'antd';
import { TeamOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useDocumentContext } from 'components/core/DocumentProvider';
import RecipientCard from 'modules/app-views/invoices/pages/Document/tabs/RecordTab/RecipientCard';
import translateX from 'utils/translateX';

// Module-level constant avoids inline array on every render (stable reference)
const ENTITY_PROFILES = ['contact', 'customer', 'company', 'opportunity'];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ContactCollapse = ({ activeKey, onCollapseChange, showAdd = true, showEdit = true }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, viewMode } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isLocked = viewMode === 'view';

  // RecipientCard derives entity keys from its name/contactName props (e.g. name="entity"
  // → "entityEntity", contactName="personContact" → "personContactEntity"). The CRM backend
  // aggregation now returns these as "__entity" / "__personContact" (double-underscore prefix
  // prevents Mongoose path conflicts). Bridge them here so RecipientCard finds them.
  const patchedData = useMemo(() => {
    if (!data) return data;
    return {
      ...data,
      entityEntity: data['__entity'] ?? data.entityEntity,
      personContactEntity: data['__personContact'] ?? data.personContactEntity,
    };
  }, [data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Actions exposed by RecipientCard (for Edit/New buttons in Collapse header)
  const [contactActions, setContactActions] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleContactActionsChange = useCallback(actions => {
    setContactActions(actions);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // Edit/New buttons rendered in the Collapse header
  const hasAnyButton = !isLocked && contactActions && (showAdd || showEdit);
  const headerExtra = hasAnyButton ? (
    <Space
      size={4}
      onClick={e => e.stopPropagation()} // Prevent collapse toggle when clicking buttons
    >
      {showAdd && (
        <Tooltip title={translateX('new_entity')} placement="top">
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={contactActions.handleNewRecipient}
          />
        </Tooltip>
      )}
      {showEdit && (
        <Tooltip title={translateX('edit_entity')} placement="top">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={contactActions.handleEditRecipient}
            disabled={!contactActions.selectedRecipient || contactActions.isSystemGenericEntity}
          />
        </Tooltip>
      )}
    </Space>
  ) : null;

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Collapse
      size="small"
      activeKey={activeKey}
      onChange={onCollapseChange}
      style={{ marginBottom: 12 }}
      styles={{
        header: { alignItems: 'center' },
        body: { padding: 20 },
      }}
      items={[
        {
          key: 'entity',
          forceRender: true,
          label: (
            <span style={{ display: 'inline-flex', alignItems: 'center', height: 40 }}>
              <TeamOutlined style={{ marginRight: 6 }} />
              {translateX('entity')}
            </span>
          ),
          extra: headerExtra,
          children: (
            <RecipientCard
              name="entity"
              label="search_entity"
              entityProfiles={ENTITY_PROFILES}
              contactName="personContact"
              data={patchedData}
              viewMode={viewMode}
              onActionsChange={handleContactActionsChange}
            />
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ContactCollapse;
